import { parse, ParseResult } from "papaparse";

export interface CSVRow {
  [key: string]: string;
}

export interface CSVFile {
  rows: CSVRow[];
}

/**
 * Strips a leading title row from CSV text if present. A title row is a single
 * line with no delimiters (commas) that people sometimes add as a label above
 * the real header row.
 */
function stripTitleRow(text: string): string {
  const firstNewline = text.indexOf("\n");
  if (firstNewline === -1) {
    return text;
  }

  const firstLine = text.slice(0, firstNewline).trim();
  const secondLine =
    text
      .slice(firstNewline + 1)
      .split("\n")[0]
      ?.trim() ?? "";

  const firstLineCommas = (firstLine.match(/,/g) || []).length;
  const secondLineCommas = (secondLine.match(/,/g) || []).length;

  if (firstLineCommas === 0 && secondLineCommas > 0) {
    return text.slice(firstNewline + 1);
  }

  return text;
}

async function prepareCleanCsvFile(file: File): Promise<File> {
  const text = stripTitleRow(await file.text());
  const blob = new Blob([text], { type: "text/csv" });
  return new File([blob], file.name, { type: "text/csv" });
}

export async function parseCSVHeaders(file: File): Promise<string[]> {
  const cleanFile = await prepareCleanCsvFile(file);

  return new Promise((resolve, reject) => {
    parse(cleanFile, {
      header: true,
      preview: 1,
      skipEmptyLines: true,
      complete: (results: ParseResult<Record<string, string>>) => {
        resolve(results.meta.fields ?? []);
      },
      error: (error) => reject(error),
    });
  });
}

export async function processCSV(file: File): Promise<CSVFile> {
  const cleanFile = await prepareCleanCsvFile(file);

  return new Promise((resolve, reject) => {
    parse(cleanFile, {
      header: true,
      skipEmptyLines: true,
      complete: (results: ParseResult<Record<string, string>>) => {
        resolve({ rows: results.data });
      },
      error: (error) => reject(error),
    });
  });
}

/**
 * Header row exactly as the import server will see it: first non-empty record,
 * comma-delimited, no title-row stripping. Reads only the first chunk of the file.
 *
 * `step` rather than `preview: 1`: papaparse applies `preview` before it drops
 * empty lines, so a file with a leading blank line would yield no header at all.
 */
export async function parseCSVHeadersExact(file: File): Promise<string[]> {
  return new Promise((resolve, reject) => {
    let headers: string[] | undefined;
    parse<string[]>(file, {
      header: false,
      delimiter: ",",
      skipEmptyLines: true,
      step: (results, parser) => {
        headers = results.data;
        parser.abort();
      },
      complete: () => resolve(headers ?? []),
      error: (error) => reject(error),
    });
  });
}
